import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

const MAX_AXES = 5;
const MAX_VARIANTS = 250;
const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function cleanKey(value, fallback = "opcion") {
  const key = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
  return key || fallback;
}

function splitValues(value) {
  const seen = new Set();
  return String(value || "")
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter((item) => {
      if (!item) return false;
      const key = item.toLocaleLowerCase("es");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function combinationKey(attributes = {}, axes = []) {
  return axes
    .map((axis) => `${axis.key}:${String(attributes?.[axis.key] || "").toLocaleLowerCase("es")}`)
    .join("|");
}

function cartesian(axes = []) {
  return axes.reduce(
    (rows, axis) =>
      rows.flatMap((row) =>
        axis.values.map((value) => ({ ...row, [axis.key]: value })),
      ),
    [{}],
  );
}


export default function ProductVariantsEditor({
  form,
  setForm,
  imageOptions = [],
  compact = false,
}) {
  const axes = Array.isArray(form.variantAttributes) ? form.variantAttributes : [];
  const variants = Array.isArray(form.variants) ? form.variants : [];
  const completeAxes = axes.filter((axis) => axis.label && axis.values?.length);
  const combinationCount = completeAxes.length ? cartesian(completeAxes).length : 0;
  const [variantPage, setVariantPage] = React.useState(0);
  const compactPageSize = 10;
  const compactPageCount = Math.max(1, Math.ceil(variants.length / compactPageSize));
  const compactVariants = compact
    ? variants.slice(variantPage * compactPageSize, (variantPage + 1) * compactPageSize)
    : variants;

  React.useEffect(() => {
    setVariantPage((current) => Math.min(current, compactPageCount - 1));
  }, [compactPageCount]);

  const updateForm = (patch) => setForm((current) => ({ ...current, ...patch }));

  const toggleVariants = (enabled) => {
    updateForm({
      hasVariants: enabled,
      variantAttributes: enabled
        ? axes.length
          ? axes
          : [{ key: "color", label: "Color", values: [], displayType: "color" }]
        : [],
      variants: enabled ? variants : [],
    });
  };

  const addAxis = () => {
    if (axes.length >= MAX_AXES) return;
    const index = axes.length + 1;
    updateForm({
      variantAttributes: [
        ...axes,
        {
          key: `opcion_${index}`,
          label: "",
          values: [],
          displayType: "button",
        },
      ],
    });
  };

  const updateAxis = (index, patch) => {
    updateForm({
      variantAttributes: axes.map((axis, axisIndex) =>
        axisIndex === index ? { ...axis, ...patch } : axis,
      ),
    });
  };

  const removeAxis = (index) => {
    const removed = axes[index];
    const nextAxes = axes.filter((_, axisIndex) => axisIndex !== index);
    updateForm({
      variantAttributes: nextAxes,
      variants: variants.map((variant) => {
        const nextAttributes = { ...(variant.attributes || {}) };
        delete nextAttributes[removed?.key];
        return { ...variant, attributes: nextAttributes };
      }),
    });
  };

  const generateVariants = () => {
    const usedKeys = new Set();
    const normalizedAxes = axes
      .map((axis, index) => {
        const label = String(axis.label || "").trim();
        const baseKey = cleanKey(axis.key || label, `opcion_${index + 1}`);
        let key = baseKey;
        let suffix = 2;
        while (usedKeys.has(key)) {
          key = `${baseKey}_${suffix}`;
          suffix += 1;
        }
        usedKeys.add(key);
        return {
          key,
          label,
          values: Array.isArray(axis.values) ? axis.values.filter(Boolean) : [],
          displayType: ["button", "select", "color"].includes(axis.displayType)
            ? axis.displayType
            : "button",
        };
      })
      .filter((axis) => axis.label && axis.values.length);

    if (!normalizedAxes.length) return;
    const combinations = cartesian(normalizedAxes);
    if (combinations.length > MAX_VARIANTS) return;
    const previous = new Map(
      variants.map((variant) => [combinationKey(variant.attributes, normalizedAxes), variant]),
    );
    const nextVariants = combinations.map((attributes) => {
      const saved = previous.get(combinationKey(attributes, normalizedAxes));
      return {
        id: saved?.id || saved?._id || "",
        attributes,
        sku: saved?.sku || "",
        barcode: saved?.barcode || "",
        price: saved?.price ?? "",
        imageRef: saved?.imageRef || "",
        active: saved?.active !== false,
      };
    });

    updateForm({
      variantAttributes: normalizedAxes,
      variants: nextVariants,
    });
    setVariantPage(0);
  };

  const updateVariant = (index, patch) => {
    updateForm({
      variants: variants.map((variant, variantIndex) =>
        variantIndex === index ? { ...variant, ...patch } : variant,
      ),
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1.5}
        >
          <Box>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
              Variantes del ítem
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Usalas cuando el mismo ítem cambia por talle, color, modelo, capacidad,
              material u otra opción elegible.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.hasVariants)}
                onChange={(event) => toggleVariants(event.target.checked)}
              />
            }
            label="Tiene variantes"
          />
        </Stack>

        {!form.hasVariants ? (
          <Alert severity="info">
            El ítem seguirá usando una única presentación, SKU y precio. Las existencias se administran exclusivamente desde Movimientos.
          </Alert>
        ) : (
          <>
            <Divider />
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                spacing={1}
              >
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                    1. Características que cambian
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ejemplo: Color = Negro, Blanco · Talle = 38, 39, 40.
                  </Typography>
                </Box>
                <Button
                  type="button"
                  startIcon={<AddRoundedIcon />}
                  onClick={addAxis}
                  disabled={axes.length >= MAX_AXES}
                  sx={{ textTransform: "none", fontWeight: 850 }}
                >
                  Agregar característica
                </Button>
              </Stack>

              {axes.map((axis, index) => (
                <Stack
                  key={`${axis.key}-${index}`}
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems={{ md: "center" }}
                >
                  <TextField
                    label="Nombre"
                    value={axis.label || ""}
                    onChange={(event) =>
                      updateAxis(index, {
                        label: event.target.value,
                        key:
                          axis.key && !axis.key.startsWith("opcion_")
                            ? axis.key
                            : cleanKey(event.target.value, `opcion_${index + 1}`),
                      })
                    }
                    placeholder="Color, Talle, Modelo..."
                    sx={{ minWidth: { md: 180 } }}
                  />
                  <TextField
                    label="Opciones"
                    value={axis.valuesText ?? (axis.values || []).join(", ")}
                    onChange={(event) =>
                      updateAxis(index, {
                        valuesText: event.target.value,
                        values: splitValues(event.target.value),
                      })
                    }
                    placeholder="Negro, Blanco, Rojo"
                    helperText="Separá cada opción con coma"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Selector"
                    value={axis.displayType || "button"}
                    onChange={(event) =>
                      updateAxis(index, { displayType: event.target.value })
                    }
                    sx={{ minWidth: { md: 150 } }}
                  >
                    <MenuItem value="button">Botones</MenuItem>
                    <MenuItem value="color">Color</MenuItem>
                    <MenuItem value="select">Lista</MenuItem>
                  </TextField>
                  <IconButton
                    type="button"
                    onClick={() => removeAxis(index)}
                    aria-label="Eliminar característica"
                    sx={{ color: BRAND.orange }}
                  >
                    <DeleteRoundedIcon />
                  </IconButton>
                </Stack>
              ))}

              <Button
                type="button"
                variant="contained"
                startIcon={<AutoAwesomeRoundedIcon />}
                onClick={generateVariants}
                disabled={!combinationCount || combinationCount > MAX_VARIANTS}
                sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, textTransform: "none", fontWeight: 900, minHeight: 42 }}
              >
                Generar combinaciones
              </Button>
              {combinationCount > MAX_VARIANTS ? (
                <Alert severity="warning">
                  La combinación supera {MAX_VARIANTS} variantes. Reducí opciones antes de generar.
                </Alert>
              ) : null}
            </Stack>

            {variants.length ? (
              <>
                <Divider />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`${variants.length} variantes`} />
                  <Chip label="Precio vacío = hereda precio general" variant="outlined" />
                </Stack>
                <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                  2. SKU, precio e imagen por combinación
                </Typography>
                {compact ? (
                  <Stack spacing={1.2}>
                    {compactVariants.map((variant, offset) => {
                      const index = variantPage * compactPageSize + offset;
                      return (
                        <Paper
                          key={variant.id || combinationKey(variant.attributes, axes) || index}
                          variant="outlined"
                          sx={{ p: 1.35, borderRadius: 2.5, bgcolor: "rgba(25,36,47,.018)" }}
                        >
                          <Stack spacing={1.2}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                                  {axes.map((axis) => variant.attributes?.[axis.key] || "—").join(" · ")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Variante {index + 1} de {variants.length}
                                </Typography>
                              </Box>
                              <FormControlLabel
                                sx={{ m: 0, flex: "0 0 auto" }}
                                control={
                                  <Switch
                                    size="small"
                                    checked={variant.active !== false}
                                    onChange={(event) => updateVariant(index, { active: event.target.checked })}
                                  />
                                }
                                label={<Typography variant="caption" sx={{ fontWeight: 800 }}>Activa</Typography>}
                              />
                            </Stack>
                            <TextField
                              size="small"
                              label="SKU"
                              value={variant.sku || ""}
                              onChange={(event) => updateVariant(index, { sku: event.target.value })}
                              placeholder="SKU único"
                              fullWidth
                            />
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                              <TextField
                                size="small"
                                label="Precio propio"
                                type="number"
                                value={variant.price ?? ""}
                                onChange={(event) => updateVariant(index, { price: event.target.value })}
                                placeholder={String(form.price || 0)}
                                inputProps={{ min: 0, step: "0.01" }}
                                fullWidth
                              />
                            </Box>
                            <TextField
                              select
                              size="small"
                              label="Imagen"
                              value={variant.imageRef || ""}
                              onChange={(event) => updateVariant(index, { imageRef: event.target.value })}
                              fullWidth
                            >
                              <MenuItem value="">Imagen principal</MenuItem>
                              {imageOptions.map((option, imageIndex) => (
                                <MenuItem key={option.ref || imageIndex} value={option.ref}>
                                  Imagen {imageIndex + 1}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                        </Paper>
                      );
                    })}
                    {variants.length > compactPageSize ? (
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Button
                          type="button"
                          size="small"
                          disabled={variantPage === 0}
                          onClick={() => setVariantPage((current) => Math.max(0, current - 1))}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Anteriores
                        </Button>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "rgba(25,36,47,.60)" }}>
                          {variantPage * compactPageSize + 1}-{Math.min((variantPage + 1) * compactPageSize, variants.length)} de {variants.length}
                        </Typography>
                        <Button
                          type="button"
                          size="small"
                          disabled={variantPage >= compactPageCount - 1}
                          onClick={() => setVariantPage((current) => Math.min(compactPageCount - 1, current + 1))}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Siguientes
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                ) : (
                <TableContainer sx={{ maxHeight: 520, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Combinación</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell>Precio propio</TableCell>
                        <TableCell>Imagen</TableCell>
                        <TableCell align="center">Activa</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {variants.map((variant, index) => (
                        <TableRow key={variant.id || combinationKey(variant.attributes, axes) || index}>
                          <TableCell sx={{ minWidth: 170 }}>
                            <Stack spacing={0.4}>
                              {axes.map((axis) => (
                                <Typography key={axis.key} variant="body2">
                                  <strong>{axis.label}:</strong> {variant.attributes?.[axis.key] || "—"}
                                </Typography>
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <TextField
                              size="small"
                              value={variant.sku || ""}
                              onChange={(event) => updateVariant(index, { sku: event.target.value })}
                              placeholder="SKU único"
                              fullWidth
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 130 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={variant.price ?? ""}
                              onChange={(event) => updateVariant(index, { price: event.target.value })}
                              placeholder={String(form.price || 0)}
                              inputProps={{ min: 0, step: "0.01" }}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 170 }}>
                            <TextField
                              select
                              size="small"
                              value={variant.imageRef || ""}
                              onChange={(event) => updateVariant(index, { imageRef: event.target.value })}
                              fullWidth
                            >
                              <MenuItem value="">Imagen principal</MenuItem>
                              {imageOptions.map((option, imageIndex) => (
                                <MenuItem key={option.ref || imageIndex} value={option.ref}>
                                  Imagen {imageIndex + 1}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              size="small"
                              checked={variant.active !== false}
                              onChange={(event) => updateVariant(index, { active: event.target.checked })}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                )}
              </>
            ) : (
              <Alert severity="warning">
                Completá las opciones y generá las combinaciones para poder guardar variantes.
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}
